import React from 'react';
import { SvgIcon } from 'src/features/nav/components/Toolbar/SvgIcon';
import {
  AppNameSectionWrapper,
  BreadcrumbsPageName,
  CategoryTitleWrapper,
  IconWrapper,
  PageHeaderSectionWrapper,
} from './styles';
import { Text } from '@amzn/storm-ui';
import { connect } from 'react-redux';
import {
  PageTitleTag,
  SelectedToolbarItem,
  selectLandingAppName,
  selectPageHeaderTagId,
  selectRenderTagInPageName,
  selectSelectedToolbarItem,
  updateLandingAppName,
} from 'src/features/nav/store/slice';
import { RootState } from 'src/app/store';
import { SuspenseIntroWidgetAnchor } from 'src/commons/utils/introWidgetAnchor';
import { InvertedTag } from 'src/commons/styles';
import { useBreadcrumbTruncationContext } from '../BreadcrumbIA2/BreadcrumbTruncationContext';
import {
  LANDING_PLACEMENT,
  LandingAppIcon,
} from 'src/commons/components/LandingAppIcon/LandingAppIcon';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { selectIsSpoofing } from '../../store/slice';

interface AppNameSectionProps {
  chromeConfigAppName: string;
  selectedToolbarItem: SelectedToolbarItem | undefined;
  isMobile?: boolean;
  introWidgetAnchorId?: string;
  pageTitleTag: PageTitleTag | null;
  landingAppName: string;
  isSpoofing: boolean;
  updateLandingAppName: (...args: any[]) => any;
}

export const AppNameSection: React.FC<AppNameSectionProps> = ({
  chromeConfigAppName,
  selectedToolbarItem,
  isMobile = false,
  introWidgetAnchorId,
  pageTitleTag,
  landingAppName,
  isSpoofing,
  updateLandingAppName,
}) => {
  const { pageTitleRef, truncatedOverflow } = useBreadcrumbTruncationContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const internalAppName = internalChromeConfig?.internalApplicationName ?? '';
  let url,
    category,
    iconPath,
    selectedAppName,
    shouldDisplayCategory,
    isInternal;
  let shouldRenderIcon, shouldRenderCategory;

  if (selectedToolbarItem) {
    ({
      url,
      category,
      appName: selectedAppName,
      iconPath,
      shouldDisplayCategory,
      isInternal,
    } = selectedToolbarItem);
    shouldRenderIcon = iconPath && !isMobile;
    shouldRenderCategory = shouldDisplayCategory && !isMobile;
  } else {
    url = window.location.href;
    shouldRenderIcon = false;
    shouldRenderCategory = false;
  }

  const appName = selectedAppName ?? chromeConfigAppName;
  const tagType = pageTitleTag?.type;
  let ariaLabel = `${appName}.`;
  ariaLabel = shouldRenderCategory ? `${category}: ${ariaLabel}` : ariaLabel;
  ariaLabel = tagType ? `${ariaLabel} ${tagType}` : ariaLabel;
  const shouldShowLandingIcon = !isSpoofing && !isInternal && !!internalAppName;

  return (
    <AppNameSectionWrapper showDivider={!isMobile && !truncatedOverflow}>
      <PageHeaderSectionWrapper
        aria-label={ariaLabel}
        href={url}
        ref={pageTitleRef}
      >
        {shouldRenderIcon && (
          <IconWrapper>
            <SvgIcon
              iconPath={iconPath}
              ariaLabel={category}
              isSelected={true}
              size="small"
              withGradient={true}
            />
          </IconWrapper>
        )}
        <CategoryTitleWrapper>
          <BreadcrumbsPageName
            type="h1"
            fontSize="base"
            data-ccx-e2e-id="aac-page-title"
          >
            {shouldRenderCategory && <Text fontSize="small">{category}</Text>}
            {appName}
            {introWidgetAnchorId ? (
              <SuspenseIntroWidgetAnchor id={introWidgetAnchorId} />
            ) : (
              pageTitleTag && (
                <InvertedTag
                  type={tagType}
                  label={pageTitleTag.label}
                  description={pageTitleTag.description ?? pageTitleTag.label}
                />
              )
            )}
          </BreadcrumbsPageName>
        </CategoryTitleWrapper>
      </PageHeaderSectionWrapper>
      {shouldShowLandingIcon && (
        <LandingAppIcon
          appName={internalAppName}
          landingAppName={landingAppName}
          tooltipLabel={appName}
          landingPlacement={LANDING_PLACEMENT.HEADER}
          updateLandingAppName={updateLandingAppName}
        />
      )}
    </AppNameSectionWrapper>
  );
};

const connector = connect(
  (state: RootState) => {
    return {
      selectedToolbarItem: selectSelectedToolbarItem(state),
      introWidgetAnchorId: selectPageHeaderTagId(state),
      pageTitleTag: selectRenderTagInPageName(state),
      landingAppName: selectLandingAppName(state),
      isSpoofing: selectIsSpoofing(state),
    };
  },
  (dispatch) => {
    return {
      updateLandingAppName: (category) =>
        dispatch(updateLandingAppName(category)),
    };
  },
);

export const ConnectedAppNameSection = connector(AppNameSection);
